/**
 * Google Analytics, conectado de verdad.
 *
 * Se pega el ID de medición de GA4 (`G-…`) y queda en los ajustes del sitio
 * (`siteSettings.googleAnalytics`). La etiqueta no se publica dentro de las
 * páginas: el worker la añade al servirlas (ver `services/googleAnalytics.ts`),
 * así que conectar, cambiar o quitar vale al instante, sin republicar.
 *
 * El consentimiento va resuelto por el país de quien visita —aviso previo en
 * la UE, el Reino Unido y Suiza; sin aviso en el resto— y la pantalla lo dice,
 * porque es lo primero que preocupa a quien conecta GA.
 */
import { useEffect, useState } from 'react'
import { IconChartBar, IconCheck, IconExternalLink, IconPlugConnected, IconShieldCheck } from '@tabler/icons-react'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { GoogleAnalyticsMark } from './GoogleAnalyticsMark'
import { getProjectSiteSettings, updateSiteSettings } from '../../../lib/api'
import { useI18n, translateServerError } from '../../../lib/i18n'

const GA_HOME = 'https://analytics.google.com/'

/** El ID de lo escrito: tal cual, o sacado de la etiqueta pegada entera. */
export function measurementIdFrom(raw: string): string | null {
  const match = raw.toUpperCase().match(/\bG-[A-Z0-9]{4,15}\b/)
  return match ? match[0] : null
}

export function GoogleAnalyticsSettings({ projectId, token, projectRole = 'owner', onBack, onOpenAnalytics }: {
  projectId: string
  token: string
  projectRole?: ProjectAccessRole
  onBack?: (() => void) | undefined
  onOpenAnalytics: () => void
}) {
  const { t } = useI18n()
  const canManage = can(projectRole, 'project.settings')
  const [saved, setSaved] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  useEffect(() => {
    let alive = true
    getProjectSiteSettings(token, projectId)
      .then((project) => { if (alive) setSaved(project.siteSettings?.googleAnalytics?.measurementId ?? null) })
      .catch(() => { if (alive) setSaved(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [projectId, token])

  const detected = measurementIdFrom(draft)
  const connected = Boolean(saved)

  async function save(measurementId: string | null) {
    setSaving(true)
    setError(null)
    try {
      const next = await updateSiteSettings(token, projectId, { googleAnalytics: measurementId ? { measurementId } : null })
      setSaved(next.googleAnalytics?.measurementId ?? null)
      setDraft('')
      setConfirmingDisconnect(false)
    } catch (err) {
      setError(translateServerError(err, t('ga.saveFailed')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ProjectSettingsLayout title={t('ga.title')} description={t('ga.description')} onBack={onBack}>
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon dash-settings-brand-icon" aria-hidden="true">
            <GoogleAnalyticsMark size={20} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t(loading ? 'ga.checking' : connected ? 'ga.connected' : 'ga.notConnected')}</h3>
            <p>{loading ? t('ga.checkingBody') : connected ? t('ga.connectedBody', { id: saved! }) : t('ga.emptyBody')}</p>
          </div>
          {connected && !loading ? (
            <span className="dash-connect-status is-ok">
              <IconCheck size={13} aria-hidden="true" />
              {t('ga.statusOk')}
            </span>
          ) : null}
        </div>

        {canManage ? (
          <>
            <label className="dash-field">
              <span>{t(connected ? 'ga.changeId' : 'ga.idLabel')}</span>
              <input
                value={draft}
                autoComplete="off"
                spellCheck={false}
                placeholder={t('ga.placeholder')}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter' && detected) void save(detected) }}
                disabled={saving}
              />
            </label>
            <p className="dash-card-caption">
              {draft.trim() ? (detected ? t('ga.detected', { id: detected }) : t('ga.notDetected')) : t('ga.where')}{' '}
              <a href={GA_HOME} target="_blank" rel="noopener noreferrer">
                {t('ga.openGa')} <IconExternalLink size={12} aria-hidden="true" />
              </a>
            </p>

            {error ? <div className="dash-inline-msg"><span>{error}</span></div> : null}

            <div className="dash-settings-card-foot">
              <button type="button" className="dash-primary-btn" onClick={() => detected && void save(detected)} disabled={saving || !detected || detected === saved}>
                <IconPlugConnected size={14} aria-hidden="true" />
                {t(connected ? 'ga.saveNew' : 'ga.connect')}
              </button>
              {connected ? (
                confirmingDisconnect ? (
                  <>
                    <button type="button" className="dash-danger-btn" onClick={() => void save(null)} disabled={saving}>{t('ga.confirmDisconnect')}</button>
                    <button type="button" className="dash-ghost-btn" onClick={() => setConfirmingDisconnect(false)} disabled={saving}>{t('ga.cancel')}</button>
                  </>
                ) : (
                  <button type="button" className="dash-secondary-btn" onClick={() => setConfirmingDisconnect(true)} disabled={saving}>{t('ga.disconnect')}</button>
                )
              ) : null}
            </div>
            {confirmingDisconnect ? <p className="dash-card-caption">{t('ga.disconnectNote')}</p> : null}
          </>
        ) : (
          <p className="dash-card-caption">{t('ga.adminOnly')}</p>
        )}
      </section>

      {/* Lo que más preocupa al conectar GA, contestado en la misma pantalla. */}
      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconShieldCheck size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('ga.privacyTitle')}</h3>
          </div>
        </div>
        <ol className="dash-connect-steps">
          <li><span className="dash-connect-step-num">1</span>{t('ga.privacy1')}</li>
          <li><span className="dash-connect-step-num">2</span>{t('ga.privacy2')}</li>
          <li><span className="dash-connect-step-num">3</span>{t('ga.privacy3')}</li>
        </ol>
      </section>

      <section className="dash-settings-card dash-general-card">
        <div className="dash-settings-card-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconChartBar size={18} />
          </span>
          <div className="dash-settings-card-head-copy">
            <h3>{t('ga.builtIn')}</h3>
            <p>{t('ga.builtInBody')}</p>
          </div>
        </div>
        <div className="dash-settings-card-foot">
          <button type="button" className="dash-secondary-btn" onClick={onOpenAnalytics}>{t('ga.open')}</button>
        </div>
      </section>
    </ProjectSettingsLayout>
  )
}
